import * as React from 'react';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Button, TextField } from '@mui/material';
import { Notifications } from '../../store/slices';
import { setCancelOrder, setDeclineOrder } from '../../api';
import { AppDialog } from '../ui';
import { GENERIC_ERROR_MESSAGE } from '../../lib/errorMessage';

/** Cancel or decline the whole order; a reason is required and goes into the order history. */
interface OrderCancelFormProps {
    getOrder: () => void;
    isPopUp: boolean;
    popUpHandler: (closed?: boolean) => void;
    actionType?: 'cancel' | 'decline';
}

export default function OrderCancelForm({ getOrder, isPopUp, popUpHandler, actionType = 'cancel' }: OrderCancelFormProps) {
    const [cancelMessage, setCancelMessage] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [pending, setPending] = useState(false);
    const dispatch = useDispatch();
    const { id, companyId } = useParams();
    const verb = actionType === 'cancel' ? 'Cancel' : 'Decline';

    useEffect(() => {
        if (isPopUp) {
            setCancelMessage('');
            setError(null);
        }
    }, [isPopUp]);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setCancelMessage(event.target.value);
        setError(event.target.value.length < 1 ? 'A reason is required' : null);
    };

    const submitAction = async () => {
        if (pending) return;
        setPending(true);
        try {
            const request = actionType === 'cancel' ? setCancelOrder : setDeclineOrder;
            await request({
                companyId,
                orderId: id,
                cancelMessage,
                onSuccess: () => {
                    dispatch(Notifications.setNotifications({ text: actionType === 'cancel' ? 'Order canceled' : 'Order declined', type: 'success' }));
                    popUpHandler(true);
                    getOrder();
                },
            });
        } catch (error) {
            console.warn(`[orders] ${actionType} failed`, error);
            dispatch(Notifications.setNotifications({ text: GENERIC_ERROR_MESSAGE, type: 'error' }));
        } finally {
            setPending(false);
        }
    };

    const submit = () => {
        if (cancelMessage.length < 1) {
            setError('A reason is required');
            return;
        }
        setError(null);
        submitAction();
    };

    return (
        <AppDialog
            open={Boolean(isPopUp)}
            onClose={() => popUpHandler(true)}
            title={`${verb} order ${id}`}
            description={actionType === 'cancel'
                ? 'The order is canceled for the client. The reason is stored in the order history.'
                : 'The order is declined and sent back to the client. The reason is stored in the order history.'}
            pending={pending}
            maxWidth="xs"
            actions={(
                <>
                    <Button variant="outlined" onClick={() => popUpHandler(true)}>Close</Button>
                    <Button variant="contained" color="error" onClick={submit} disabled={pending}>{verb} order</Button>
                </>
            )}
        >
            <TextField
                label={`Reason for ${actionType === 'cancel' ? 'canceling' : 'declining'}`}
                name="reason"
                multiline
                rows={4}
                fullWidth
                value={cancelMessage}
                onChange={handleInputChange}
                error={Boolean(error)}
                helperText={error}
                required
                autoFocus
            />
        </AppDialog>
    );
}
